import React, { useState } from "react";
import { Sparkles, Mail } from "lucide-react";

export const NewsletterSubscription: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail("");
    }, 1000);
  };

  if (isSubscribed) {
    return (
      <section className="py-16 bg-gradient-to-r from-purple-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-white mb-4">
              Thank You for Subscribing!
            </h2>
            <p className="text-purple-100 text-lg mb-6">
              You'll receive our latest offers and toy updates in your inbox
              soon.
            </p>
            <button
              onClick={() => setIsSubscribed(false)}
              className="bg-white text-purple-600 font-semibold py-2 px-6 rounded-full hover:bg-purple-50 transition-colors duration-200"
            >
              Subscribe Another Email
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-r from-purple-500 to-pink-500">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-white opacity-10 rounded-full animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white opacity-10 rounded-full animate-pulse delay-2000"></div>
        </div>

        <div className="relative text-center">
          {/* Section Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-yellow-300 mr-3" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Never Miss a Deal!
              </h2>
            </div>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Subscribe to our newsletter for exclusive offers and early access
              to sales!
            </p>
          </div>

          {/* Subscription Form */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 max-w-2xl mx-auto">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-4"
            >
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-purple-200" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-purple-200 rounded-lg leading-5 bg-white bg-opacity-20 placeholder-purple-200 text-white focus:outline-none focus:placeholder-purple-100 focus:ring-2 focus:ring-white focus:border-transparent sm:text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className={`px-8 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                  isLoading || !email
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <span className="text-lg mr-2 animate-bounce">📧</span>
                    Subscribing...
                  </div>
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>

            {/* Additional Info */}
            <div className="mt-6 text-center">
              <p className="text-purple-100 text-sm">
                Join over 10,000 happy customers who get the best deals first!
              </p>
              <div className="flex justify-center items-center mt-4 space-x-6 text-xs text-purple-200">
                <span>✓ No spam, ever</span>
                <span>✓ Unsubscribe anytime</span>
                <span>✓ Weekly updates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSubscription;
